import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import Select from "react-select";
import { all_routes } from "../../../router/backendComponents/admin/all_routes";
import { isAuthenticated } from "../../../authentication/backendModule/admin/auth";
import apiRequestCall from "../../../apiHelper/globalHelper";
import { parseValidationErrors } from "../../../apiHelper/validationHelper";
import Export from "../../../formHelper/export";
import Search from "../../../formHelper/search";
import Pagination from "../../../formHelper/pagination";
import Table from "../../../formHelper/list";
import DateRangePicker from "react-bootstrap-daterangepicker";
import Success from "../../../formHelper/success";
import Delete from "../../../formHelper/delete";
import exportApiCall from "../../../apiHelper/export";
import FilterDropdown from "../../../formHelper/filterDropDown";
import Loader from "../../../formHelper/Loader";
import dayjs from "dayjs";
import type { RootState, AppDispatch } from "../../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";

interface USPRow {
  id: number;
  usp_type: string;
  packing_details: string;
  usp_brand: string;
  category_id: number;
  principal_id: number;
  created_at: string;
  branch?: { name?: string };
  category_type?: { type: string };
  principal?: { type: string };
}

interface Common {
  id: number;
  type: string;
}
interface PermissionList {
  id: number;
  role_id: number;
  name: string;
  role: {
    permissions: Record<string, Record<string, string>>;
  };
}

const USP = () => {
  const backendRoute = all_routes.backend;

  const [list, setList] = useState<USPRow[]>([]);
  const [formData, setFormData] = useState<any>({
    usp_type: "",
    packing_details: "",
    usp_brand: "",
    category_id: "",
    principal_id: null,
  });

  const [formError, setFormError] = useState<{ [key: string]: string }>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [categoryList, setCategoryList] = useState<Common[]>([]);
  const [principalList, setPrincipalList] = useState<Common[]>([]);

  const [showForm, setShowForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showMessage, setShowMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const [query, setQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      return 5;
    }
    return 10;
  });
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  const [categoryFilter, setCategoryFilter] = useState<number[]>([]);
  const [uspFilter, setUspFilter] = useState<number[]>([]);
  const [brandFilter, setBrandFilter] = useState<number[]>([]);

  const [principalFilter, setPrincipalFilter] = useState<number[]>([]);
  const [categoryId, setCategoryId] = useState(null);
  // Export states
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // request guards
  const [submitting, setSubmitting] = useState(false);
  const [deletingFlag, setDeletingFlag] = useState(false);

  /** ---------- Export Helpers ---------- **/
  const triggerDownload = (url: string, filename: string, revoke = false) => {
    if (!url) return;

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setDownloading(true);
    setTimeout(() => setDownloading(false), 3000);
    setExporting(false);
  };

  // Poll API for local download
  const pollForFile = (filename: string) => {
    let attempts = 0;
    const maxAttempts = 60;

    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await exportApiCall("get", `/download-export/${filename}`, {
          responseType: "blob",
        });

        if (res) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(true);

          const blob = new Blob([res], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          triggerDownload(url, filename, true);

          setTimeout(() => setDownloading(false), 3000);
        }
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(false);
          alert("Export failed while downloading.");
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(false);
          alert("Export took too long. Please try again later.");
        }
      }
    }, 5000);
  };

  const exportFile = async () => {
    if (exporting || submitting || deletingFlag) return;
    try {
      setExporting(true);
      setDownloading(false);

      const result = await apiRequestCall("post", backendRoute.getUSP, {
        data: {
          search: query.trim(),
          start_date: startDate,
          end_date: endDate,
          category_list: categoryFilter,
          usp_list: uspFilter,
          brand_list: brandFilter,
          principal_list: principalFilter,
          download: true,
        },
      });

      const filename = result?.data?.data?.file;
      const fileUrl = result?.data?.data?.url;

      if (!filename) {
        setExporting(false);
        alert("Export request failed. Please try again.");
        return;
      }

      pollForFile(filename);
    } catch (error) {
      console.error("Export request failed:", error);
      setExporting(false);
      setDownloading(false);
      alert("Export failed. Please try again.");
    }
  };

  /** ---------- CRUD + Fetch ---------- **/
  const inputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormError((prev) => {
      const updated = { ...prev };
      delete updated[name];
      return updated;
    });
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      usp_type: "",
      packing_details: "",
      usp_brand: "",
      category_id: "",
      principal_id: null,
    });
    setFormError({});
    setSelectedId(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        usp_id: selectedId,
        update_status: !!selectedId,
      };

      const result = await apiRequestCall("post", backendRoute.addUpdateUSP, {
        data: payload,
      });

      if (result?.data?.code === 221) {
        setFormError(parseValidationErrors(result?.data?.errors));
        return;
      }

      if (result?.data?.code === 200) {
        resetForm();
        setShowSuccess(true);
        setShowMessage(result?.data?.message);
        fetchList({ page: currentPage, perPage });
      } else {
        setShowMessage(result?.data?.message || "Operation failed");
        setShowSuccess(true);
      }
    } catch (error) {
      console.error("Submit failed:", error);
      const msg =
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        "Submit failed. Please try again.";
      setShowMessage(msg);
      setShowSuccess(true);
    } finally {
      setSubmitting(false);
    }
  };

  const updateRecord = (id: number) => {
    if (submitting || deletingFlag) return;
    const selected = list.find((x) => x.id === id);
    if (!selected) return;
    setFormError({});
    setFormData({
      usp_type: selected?.usp_type ?? "",
      usp_brand: selected?.usp_brand ?? "",
      packing_details: selected?.packing_details ?? "",
      category_id: selected?.category_id ?? "",
      principal_id: selected?.principal_id ?? null,
    });

    setSelectedId(selected.id);
    setShowForm(true);
  };

  const deleteRecord = (id: number) => {
    if (submitting || deletingFlag) return;
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId || deletingFlag) return;
    setDeletingFlag(true);
    try {
      const result = await apiRequestCall("post", backendRoute.deleteUSP, {
        data: { id: deleteId },
      });
      if (result?.data?.code === 200) {
        fetchList({ page: currentPage, perPage });
        setShowMessage(result?.data?.message);
        setShowSuccess(true);
      } else {
        setShowMessage(result?.data?.message || "Delete failed");
        setShowSuccess(true);
      }
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Delete failed. Please try again.");
    } finally {
      setDeleteId(null);
      setShowDeleteModal(false);
      setDeletingFlag(false);
    }
  };

  const fetchList = async ({ page = 1, perPage: p = 10 } = {}) => {
    setLoading(true);
    try {
      const result = await apiRequestCall("post", backendRoute.getUSP, {
        data: {
          page,
          per_page: p,
          start_date: startDate,
          end_date: endDate,
          search: query.trim(),
          category_list: categoryFilter,
          usp_list: uspFilter,
          brand_list: brandFilter,
          principal_list: principalFilter,
        },
      });

      const res = result?.data?.data;
      if (res) {
        setList(res.data ?? []);
        setCurrentPage(res.current_page ?? 1);
        setPerPage(res.per_page ?? 10);
        setTotalItems(res.total ?? 0);
      } else {
        setList([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error("Failed to fetch USP list:", error);
      setList([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  /** ---------- Dropdown Data ---------- **/
  const getCategoryList = async () => {
    try {
      const result = await apiRequestCall(
        "post",
        backendRoute.getUpsCategoryDD
      );
      const raw = result?.data?.data;
      if (raw && typeof raw === "object") {
        setCategoryList(
          Object.entries(raw).map(([id, type]) => ({
            id: +id,
            type: String(type),
          }))
        );
      }
    } catch (err) {
      console.error("Failed to fetch USP categories:", err);
    }
  };

  const getPrincipalList = async () => {
    try {
      const result = await apiRequestCall("post", backendRoute.getPrincipalDD);
      const raw = result?.data?.data;
      if (raw && typeof raw === "object") {
        setPrincipalList(
          Object.entries(raw).map(([id, type]) => ({
            id: +id,
            type: String(type),
          }))
        );
      }
    } catch (err) {
      console.error("Failed to fetch principals:", err);
    }
  };

  // Manage dependent filter

  const handleCategoryChange = (e: any) => {
    setCategoryId(e?.[0]);
  };

  /** ---------- Effects ---------- **/
  useEffect(() => {
    fetchList({ page: currentPage, perPage });
  }, [
    query,
    currentPage,
    perPage,
    startDate,
    endDate,
    categoryFilter,
    uspFilter,
    brandFilter,
    principalFilter,
  ]);

  useEffect(() => {
    getPrincipalList();
    getCategoryList();
  }, []);

  const permission = useSelector(
    (state: RootState) => state.rolesPermissions?.data
  ) as PermissionList | null;

  const permissionMap = useMemo(() => {
    return permission?.role?.permissions ?? {};
  }, [permission]);

  if (!isAuthenticated()) return <Navigate to="/login" replace />;

  const columns = [
    {
      key: "id",
      label: "Sr. No",
      render: (_: any, i: number) => (currentPage - 1) * perPage + i + 1,
    },
    { key: "usp_type", label: "Type" },
    { key: "packing_details", label: "Packing" },
    { key: "usp_brand", label: "Brand" },
    { key: "category.name", label: "Category" },
    { key: "principal.type", label: "Principal" },
    { key: "branch.name", label: "Branch" },
    { key: "created_at", label: "Date" },

    ...(permissionMap?.usp?.edit_usp || permissionMap?.usp?.delete_usp
      ? [
          {
            key: "action",
            label: "Action",
            render: (row: USPRow) => (
              <div className="hstack gap-2 fs-15">
                {permissionMap?.usp?.edit_usp && (
                  <button
                    className="btn btn-icon btn-sm btn-soft-info"
                    onClick={() => updateRecord(row.id)}
                    disabled={submitting || deletingFlag}
                    title="Edit Role"
                    aria-label={`Edit ${row.usp_type}`}
                  >
                    <i className="feather-edit" />
                  </button>
                )}

                {permissionMap?.usp?.delete_usp && (
                  <button
                    className="btn btn-icon btn-sm btn-soft-danger"
                    onClick={() => deleteRecord(row.id)}
                    disabled={submitting || deletingFlag}
                    title="Delete Role"
                    aria-label={`Delete ${row.usp_type}`}
                  >
                    <i className="feather-trash" />
                  </button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  // ---------- Render (two-card layout like Source.tsx) ----------
  return (
    <div className="page-wrapper">
      <div className="content">
        {/* compact header: title | export | new */}
        <div className="row page-header">
          <div className="col-12">
            <div className="d-flex align-items-start justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <h4 className="page-title">
                  USP
                  <span className="count-title">{totalItems}</span>
                </h4>
              </div>

              <div className="d-flex align-items-center gap-2">
                <div className="d-flex justify-content-end gap-2">
                  {permissionMap?.usp?.export_usp && (
                    <Export
                      options={[
                        {
                          label: downloading
                            ? "Downloading..."
                            : exporting
                            ? "Generating..."
                            : "Export",
                          iconClass: downloading
                            ? "ti ti-download"
                            : exporting
                            ? "ti ti-loader"
                            : "ti ti-file-type-xls",
                          colorClass:
                            exporting || downloading
                              ? "text-muted"
                              : "text-primary",
                          onClick:
                            exporting ||
                            downloading ||
                            submitting ||
                            deletingFlag
                              ? undefined
                              : exportFile,
                        },
                      ]}
                    />
                  )}

                  {permissionMap?.usp?.add_usp && (
                    <button
                      className="btn btn-soft-success d-flex align-items-center"
                      onClick={() => {
                        resetForm();
                        setShowForm(true);
                      }}
                      disabled={submitting}
                      aria-label="Add new usp"
                    >
                      <i className="ti ti-square-rounded-plus" />
                      <span className="d-none d-md-inline ms-1">New</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top card: search / date / filter */}
        <div className="card mb-2">
          <div className="card-body">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-2">
                <div className="icon-form">
                  <span className="form-icon">
                    <i className="ti ti-search" />
                  </span>
                  <Search
                    placeholder="Search USP"
                    value={query}
                    onChange={setQuery}
                  />
                </div>
              </div>

              <div className="col-12 col-md-2">
                <div className="icon-form">
                  <span className="form-icon">
                    <i className="ti ti-calendar" />
                  </span>
                  <DateRangePicker
                    initialSettings={{
                      autoUpdateInput: false,
                      locale: { cancelLabel: "Clear" },
                    }}
                    onCallback={(start, end) => {
                      setStartDate(start.format("YYYY-MM-DD"));
                      setEndDate(end.format("YYYY-MM-DD"));
                    }}
                    onCancel={() => {
                      setStartDate("");
                      setEndDate("");
                    }}
                  >
                    <input
                      type="text"
                      className="form-control bookingrange"
                      placeholder="Select Date Range Filter"
                      value={
                        startDate && endDate
                          ? `${dayjs(startDate).format("DD-MM-YYYY")} - ${dayjs(
                              endDate
                            ).format("DD-MM-YYYY")}`
                          : ""
                      }
                      readOnly
                      style={{ cursor: "pointer" }}
                      aria-label="Select date range"
                    />
                  </DateRangePicker>
                </div>
              </div>

              <div className="col-12 col-md-2">
                <FilterDropdown
                  label="Category"
                  endpoint="/getUpsCategoryDD"
                  selected={categoryFilter}
                  onChange={(v) => {
                    setCategoryFilter(v as number[]);
                    handleCategoryChange(v);
                  }}
                />
              </div>

              <div className="col-12 col-md-2">
                <FilterDropdown
                  label="USP"
                  endpoint={
                    categoryId
                      ? `/getUspDD?category_id=${categoryId}`
                      : "/getUspDD"
                  }
                  selected={uspFilter}
                  onChange={(v) => setUspFilter(v as number[])}
                />
              </div>

              <div className="col-12 col-md-2">
                <FilterDropdown
                  label="Brand"
                  endpoint={
                    categoryId
                      ? `/getUspBrandDD?category_id=${categoryId}`
                      : "/getUspBrandDD"
                  }
                  selected={brandFilter}
                  onChange={(v) => setBrandFilter(v as number[])}
                />
              </div>

              <div className="col-12 col-md-2">
                <FilterDropdown
                  label="Principal"
                  endpoint={
                    categoryId
                      ? `/getUspPrincipalDD?category_id=${categoryId}`
                      : "/getPrincipalDD"
                  }
                  selected={principalFilter}
                  onChange={(v) => setPrincipalFilter(v as number[])}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table card */}
        <div className="card">
          <div className="card-body">
            {loading ? (
              <Loader />
            ) : (
              <div className="table-responsive">
                <Table data={list} columns={columns} />
              </div>
            )}
          </div>

          <Pagination
            currentPage={currentPage}
            perPage={perPage}
            totalItems={totalItems}
            onPageChange={(p: number) => {
              setCurrentPage(p);
              fetchList({ page: p, perPage });
            }}
            onPerPageChange={(n: number) => {
              setPerPage(n);
              setCurrentPage(1);
            }}
            perPageOptions={[
              { value: 5, label: "5" },
              { value: 10, label: "10" },
              { value: 25, label: "25" },
            ]}
          />
        </div>

        {/* Offcanvas Form */}
        <div
          className={`offcanvas offcanvas-end offcanvas-large ${
            showForm ? "show" : ""
          }`}
          style={{
            visibility: showForm ? "visible" : "hidden",
          }}
          tabIndex={-1}
          aria-modal={showForm}
          role="dialog"
        >
          <div className="offcanvas-header border-bottom">
            <h5 className="fw-semibold">
              {selectedId ? "Update USP" : "Add USP"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => !submitting && setShowForm(false)}
              disabled={submitting}
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>

          <div className="offcanvas-body">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!submitting) handleSubmit();
              }}
            >
              <div className="mb-3">
                <label className="col-form-label">
                  Type <span className="text-danger">*</span>
                </label>
                <input
                  name="usp_type"
                  type="text"
                  className="form-control"
                  value={formData.usp_type}
                  onChange={inputChange}
                  disabled={submitting}
                />
                {formError.usp_type && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.usp_type}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="col-form-label">
                  Packing Details <span className="text-danger">*</span>
                </label>
                <textarea
                  name="packing_details"
                  className="form-control"
                  style={{ height: "120px" }}
                  value={formData.packing_details}
                  onChange={inputChange}
                  disabled={submitting}
                />
                {formError.packing_details && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.packing_details}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="col-form-label">
                  Brand <span className="text-danger">*</span>
                </label>
                <input
                  name="usp_brand"
                  className="form-control"
                  value={formData.usp_brand}
                  onChange={inputChange}
                  disabled={submitting}
                />
                {formError.usp_brand && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.usp_brand}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="col-form-label">
                  Select Category <span className="text-danger">*</span>
                </label>
                <Select
                  options={categoryList.map((b) => ({
                    value: b.id,
                    label: b.type,
                  }))}
                  value={
                    categoryList.find((b) => b.id === formData.category_id)
                      ? {
                          value: formData.category_id,
                          label: categoryList.find(
                            (b) => b.id === formData.category_id
                          )?.type,
                        }
                      : null
                  }
                  onChange={(selected) =>
                    setFormData((prev: any) => ({
                      ...prev,
                      category_id: selected?.value || null,
                    }))
                  }
                  isClearable
                  isDisabled={submitting}
                />
                {formError.category_id && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.category_id}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="col-form-label">
                  Select Principal <span className="text-danger">*</span>
                </label>
                <Select
                  options={principalList.map((b) => ({
                    value: b.id,
                    label: b.type,
                  }))}
                  value={
                    principalList.find((b) => b.id === formData.principal_id)
                      ? {
                          value: formData.principal_id,
                          label: principalList.find(
                            (b) => b.id === formData.principal_id
                          )?.type,
                        }
                      : null
                  }
                  onChange={(selected) =>
                    setFormData((prev: any) => ({
                      ...prev,
                      principal_id: selected?.value || null,
                    }))
                  }
                  isClearable
                  isDisabled={submitting}
                />
                {formError.principal_id && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.principal_id}
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-soft-primary me-2"
                  onClick={() => setShowForm(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-soft-success"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      {selectedId ? "Updating..." : "Creating..."}
                    </>
                  ) : selectedId ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        <Success
          id="USPModal"
          title="USP"
          message={showMessage}
          show={showSuccess}
          onClose={() => setShowSuccess(false)}
        />
        <Delete
          show={showDeleteModal}
          title="Delete USP?"
          message="Are you sure?"
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      </div>
    </div>
  );
};

export default USP;
